import React, {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { LeaderboardEntry, TeamMember, PrestigeEntry, PrestigeRank } from "../types";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { logger } from "../lib/logger";

// =============================================================================
// TeamContext - Team Members, Leaderboard, Prestige Points
// =============================================================================
// PURPOSE: Manages team data with 30-second auto-refresh and dual cache TTLs.
//
// ARCHITECTURE:
// - Fetches from *_readable views (pre-joined for display)
// - Dual cache: 2-min TTL for leaderboard (changes often), 5-min for team/prestige
// - 30-second polling when tab is visible (pauses when hidden)
// - AbortController cleanup prevents race conditions on org switching
// - Company-wide leaderboard scoped to user's visible orgs (RBAC support)
//
// KEY DECISIONS:
// - Shorter TTL for leaderboard: rankings change frequently during work hours
// - Visibility-based polling: saves resources when tab is hidden
// - Company-wide scope: execs see all orgs, others see only their memberships
// - Prestige points cached longer: monthly rollup data doesn't change often
// =============================================================================

import { CACHE_VERSION } from "../lib/cacheVersion";

// --- Cache Configuration ---
const CACHE_PREFIX = `altara_${CACHE_VERSION}_team_`;
const LEADERBOARD_CACHE_TTL = 2 * 60 * 1000; // 2 minutes (refreshes more often)
const TEAM_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

// Cache helpers
const getCache = <T,>(key: string, ttl: number, orgId: string): T | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    const isExpired = Date.now() - entry.timestamp > ttl;
    const isSameOrg = entry.orgId === orgId;

    if (isExpired || !isSameOrg) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
};

const setCache = <T,>(key: string, data: T, orgId: string): void => {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), orgId };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    logger.warn("Team cache write failed");
  }
};

export const invalidateTeamCache = (orgId: string): void => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const entry = JSON.parse(raw);
            if (entry.orgId === orgId) {
              localStorage.removeItem(key);
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  } catch {
    // Ignore cleanup errors
  }
};

interface TeamContextType {
  teamMembers: TeamMember[];
  leaderboard: LeaderboardEntry[];
  companyWideLeaderboard: LeaderboardEntry[];
  prestigePoints: PrestigeEntry[];
  prestigeRanks: PrestigeRank[];
  isLoading: boolean;
  refreshLeaderboard: () => Promise<void>;
}

export const TeamContext = createContext<TeamContextType | undefined>(undefined);

interface TeamProviderProps {
  children: ReactNode;
  organizationId: string | null;
  availableOrgIds: string[];
  forceRefresh?: boolean;
}

// Combined state interface — batches all team data into a single setState to avoid
// 5-6 separate re-renders on initial load and cache hits.
interface TeamState {
  teamMembers: TeamMember[];
  leaderboard: LeaderboardEntry[];
  companyWideLeaderboard: LeaderboardEntry[];
  prestigePoints: PrestigeEntry[];
  prestigeRanks: PrestigeRank[];
  isLoading: boolean;
}

const INITIAL_TEAM_STATE: TeamState = {
  teamMembers: [],
  leaderboard: [],
  companyWideLeaderboard: [],
  prestigePoints: [],
  prestigeRanks: [],
  isLoading: false,
};

export const TeamProvider: React.FC<TeamProviderProps> = ({
  children,
  organizationId,
  availableOrgIds,
  forceRefresh = false,
}) => {
  // isLoading starts true so the first paint shows the portal's Team placeholders, not a
  // one-frame "No leaderboard data". The effect's exits (no org → INITIAL_TEAM_STATE,
  // cache hit, network success/error) all set it false. INITIAL_TEAM_STATE stays false.
  const [state, setState] = useState<TeamState>({ ...INITIAL_TEAM_STATE, isLoading: true });

  // AbortController for cleanup
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  const forceRefreshRef = useRef(forceRefresh);
  // Org whose data is currently in state. When the effect runs for a DIFFERENT org, the
  // old org's leaderboard/roster must not stay on screen while the new one loads (the
  // portal's Team section would render org A's reps under org B). A same-org rerun
  // (availableOrgIds change, force refresh) keeps showing the current data.
  const loadedOrgRef = useRef<string | null>(null);

  // Update force refresh ref when prop changes
  useEffect(() => {
    forceRefreshRef.current = forceRefresh;
  }, [forceRefresh]);

  useEffect(() => {
    // Abort any in-flight request to prevent race conditions
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    fetchAbortControllerRef.current = new AbortController();
    const signal = fetchAbortControllerRef.current.signal;

    const fetchTeamData = async () => {
      if (!organizationId || !isSupabaseConfigured || !supabase) {
        loadedOrgRef.current = null;
        setState(INITIAL_TEAM_STATE);
        return;
      }

      const shouldForceRefresh = forceRefreshRef.current;

      // Check cache first (unless forcing refresh)
      if (!shouldForceRefresh) {
        const cachedLeaderboard = getCache<LeaderboardEntry[]>(
          "leaderboard",
          LEADERBOARD_CACHE_TTL,
          organizationId
        );
        const cachedTeam = getCache<TeamMember[]>("members", TEAM_CACHE_TTL, organizationId);
        const cachedPrestige = getCache<PrestigeEntry[]>(
          "prestige",
          TEAM_CACHE_TTL,
          organizationId
        );
        const cachedCompanyLeaderboard = getCache<LeaderboardEntry[]>(
          "companyLeaderboard",
          LEADERBOARD_CACHE_TTL,
          organizationId
        );
        const cachedPrestigeRanks = getCache<PrestigeRank[]>(
          "prestigeRanks",
          TEAM_CACHE_TTL,
          organizationId
        );

        // If we have all core cached data, use it — single setState avoids multiple re-renders
        if (cachedLeaderboard && cachedTeam) {
          logger.debug("Using cached team data");
          const sameOrg = loadedOrgRef.current === organizationId;
          loadedOrgRef.current = organizationId;
          setState((prev) => ({
            ...(sameOrg ? prev : INITIAL_TEAM_STATE),
            leaderboard: cachedLeaderboard,
            teamMembers: cachedTeam,
            ...(cachedPrestige && { prestigePoints: cachedPrestige }),
            ...(cachedPrestigeRanks && { prestigeRanks: cachedPrestigeRanks }),
            ...(cachedCompanyLeaderboard && { companyWideLeaderboard: cachedCompanyLeaderboard }),
            // Must clear: a prior run may have set isLoading=true, then been aborted by
            // this run. Its aborted-bail returns without resetting, so if this cache-hit
            // path doesn't either, isLoading sticks true forever (portal skeleton bug, Oct 5).
            isLoading: false,
          }));
          return; // Exit early, using cached data
        }
      }

      if (loadedOrgRef.current === organizationId) {
        setState((prev) => ({ ...prev, isLoading: true }));
      } else {
        // State now belongs to this org (empty until the fetch lands), so a late
        // refreshLeaderboard response for the previous org is rejected below.
        loadedOrgRef.current = organizationId;
        setState({ ...INITIAL_TEAM_STATE, isLoading: true });
      }
      try {
        logger.debug("Fetching fresh team data");

        // Fetch Leaderboard (readable view)
        const leaderboardReq = supabase
          .from("daily_leaderboard_readable")
          .select("*")
          .eq("organization_id", organizationId)
          .order("date", { ascending: false })
          .limit(1000);

        // Fetch Company-Wide Leaderboard (scoped to user's visible orgs)
        const companyWideLeaderboardReq = supabase
          .from("daily_leaderboard_readable")
          .select("*")
          .in("organization_id", availableOrgIds.length > 0 ? availableOrgIds : [organizationId])
          .order("date", { ascending: false })
          .limit(3000);

        // Fetch Team Members (readable view)
        const teamReq = supabase
          .from("organization_members_readable")
          .select("*")
          .eq("organization_id", organizationId)
          .limit(200); // Safety cap: 200 team members per org

        // Fetch Prestige Points (readable view)
        // P2 fix (Jul 4 2026 diff-sweep): month-bounded window replaces the old
        // `.order("total_points", desc).limit(200)`. At 200-300 reps/org with
        // several months of monthly prestige rollups, ordering by total_points
        // across ALL history let old high-scoring months push a rep's
        // CURRENT/covered-month row past row 200 — LeaderboardPage's
        // prestige_total (mvp_points lookup) silently read 0 for that rep.
        // PGRST_MAX_ROWS=1000 rules out just raising the limit (anything past
        // 1000 silently re-truncates). Fix: filter to the last
        // PRESTIGE_MONTHS_WINDOW calendar months instead of ordering by score.
        // Every confirmed consumer (LeaderboardPage personalStats /
        // prestigeStandings: current month only; computeSectorEntries:
        // `coveredMonths` of the selected date range — every built-in preset up
        // to "Last Month" needs at most 2 months) is covered by a 3-month
        // window. 3 months × the CLAUDE.md target scale of 300 reps/org = 900
        // rows, safely under the 1000-row cap — the largest window that still
        // guarantees zero silent truncation at that scale (4 × 300 = 1200
        // would overflow). Residual accepted risk: a manually-dragged custom
        // date range reaching further back than 3 months reads mvp_points=0
        // for the out-of-window months — narrowed from "any page load once the
        // org accrues 200+ reps×months of history" to "an intentionally wide
        // custom range." See handoff / commit message for detail.
        const PRESTIGE_MONTHS_WINDOW = 3;
        const prestigeCutoffMonth = (() => {
          const d = new Date();
          d.setDate(1); // pin to day 1 first — avoids month-length rollover when subtracting
          d.setMonth(d.getMonth() - (PRESTIGE_MONTHS_WINDOW - 1));
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
        })();
        const prestigeReq = supabase
          .from("prestige_points_readable")
          .select("*")
          .eq("organization_id", organizationId)
          .gte("month", prestigeCutoffMonth)
          .order("month", { ascending: false })
          .limit(1000);

        // Fetch Prestige Ranks (persistent levels 0-12)
        const prestigeRanksReq = supabase
          .from("prestige_ranks_readable")
          .select("*")
          .eq("organization_id", organizationId)
          .limit(200);

        const [leaderboardRes, companyWideLeaderboardRes, teamRes, prestigeRes, prestigeRanksRes] =
          await Promise.all([
            leaderboardReq,
            companyWideLeaderboardReq,
            teamReq,
            prestigeReq,
            prestigeRanksReq,
          ]);

        // Bail out if request was aborted (user switched orgs)
        if (signal.aborted) {
          logger.debug("Team fetch aborted - org switch detected");
          return;
        }

        // Log errors for individual queries
        if (leaderboardRes?.error) logger.error("Leaderboard Fetch Error", leaderboardRes.error);
        if (companyWideLeaderboardRes?.error)
          logger.error("Company-Wide Leaderboard Fetch Error", companyWideLeaderboardRes.error);
        if (teamRes?.error) logger.error("Team Members Fetch Error", teamRes.error);
        if (prestigeRes?.error) logger.error("Prestige Points Fetch Error", prestigeRes.error);
        if (prestigeRanksRes?.error)
          logger.error("Prestige Ranks Fetch Error", prestigeRanksRes.error);

        // Resolve each result — fall back to empty array on error
        const lbData = leaderboardRes?.data ? (leaderboardRes.data as LeaderboardEntry[]) : [];
        const cwData = companyWideLeaderboardRes?.data
          ? (companyWideLeaderboardRes.data as LeaderboardEntry[])
          : [];
        const teamData = teamRes?.data ? (teamRes.data as TeamMember[]) : [];
        const prestigeData = prestigeRes?.data ? (prestigeRes.data as PrestigeEntry[]) : [];
        const ranksData = prestigeRanksRes?.data ? (prestigeRanksRes.data as PrestigeRank[]) : [];

        // Write to cache for successful fetches
        if (leaderboardRes?.data) setCache("leaderboard", lbData, organizationId);
        if (companyWideLeaderboardRes?.data) setCache("companyLeaderboard", cwData, organizationId);
        if (teamRes?.data) setCache("members", teamData, organizationId);
        if (prestigeRes?.data) setCache("prestige", prestigeData, organizationId);
        if (prestigeRanksRes?.data) setCache("prestigeRanks", ranksData, organizationId);

        // Single setState — one re-render instead of 5-6
        loadedOrgRef.current = organizationId;
        setState({
          teamMembers: teamData,
          leaderboard: lbData,
          companyWideLeaderboard: cwData,
          prestigePoints: prestigeData,
          prestigeRanks: ranksData,
          isLoading: false,
        });
      } catch (err) {
        logger.error("Error fetching team data", err);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchTeamData();

    // Cleanup: abort in-flight request on unmount or dependency change
    return () => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [organizationId, availableOrgIds]);

  // Leaderboard refresh function
  const refreshLeaderboard = useCallback(async () => {
    if (!organizationId || !supabase) return;

    try {
      const [lbRes, cwRes] = await Promise.all([
        supabase
          .from("daily_leaderboard_readable")
          .select("*")
          .eq("organization_id", organizationId)
          .order("date", { ascending: false })
          .limit(1000),
        supabase
          .from("daily_leaderboard_readable")
          .select("*")
          .in("organization_id", availableOrgIds.length > 0 ? availableOrgIds : [organizationId])
          .order("date", { ascending: false })
          .limit(3000),
      ]);

      // The org switched while this refresh was in flight: its rows belong to the old
      // org and must not be merged into the new org's state.
      if (loadedOrgRef.current !== organizationId) return;

      // Batch both leaderboard updates into a single setState — one re-render
      const updates: Partial<TeamState> = {};
      if (lbRes.data) {
        const lbData = lbRes.data as LeaderboardEntry[];
        setCache("leaderboard", lbData, organizationId);
        updates.leaderboard = lbData;
      }
      if (cwRes.data) {
        const cwData = cwRes.data as LeaderboardEntry[];
        setCache("companyLeaderboard", cwData, organizationId);
        updates.companyWideLeaderboard = cwData;
      }
      if (Object.keys(updates).length > 0) {
        setState((prev) => ({ ...prev, ...updates }));
      }
    } catch (err) {
      logger.error("Leaderboard refresh error", err);
    }
  }, [organizationId, availableOrgIds]);

  // Leaderboard polling - only when tab is visible
  useEffect(() => {
    if (!organizationId || !supabase) return;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshLeaderboard();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [organizationId, refreshLeaderboard]);

  const {
    teamMembers,
    leaderboard,
    companyWideLeaderboard,
    prestigePoints,
    prestigeRanks,
    isLoading,
  } = state;

  const contextValue = useMemo(
    () => ({
      teamMembers,
      leaderboard,
      companyWideLeaderboard,
      prestigePoints,
      prestigeRanks,
      isLoading,
      refreshLeaderboard,
    }),
    [
      teamMembers,
      leaderboard,
      companyWideLeaderboard,
      prestigePoints,
      prestigeRanks,
      isLoading,
      refreshLeaderboard,
    ]
  );

  return <TeamContext.Provider value={contextValue}>{children}</TeamContext.Provider>;
};
